export enum ChaseStartTimeType {
  FROM_CONDITION = 1,
  FROM_LAST_MESSAGE = 2,
}

export enum ChaseScheduleStatus {
  ACTIVE = 1,
  INACTIVE = 2,
}

export enum ChaseScheduleSendType {
  MESSAGE = 1,
  ACTION = 2,
}

export enum ChaseScheduleTriggerType {
  AFTER = 1,
  MANUAL = 2,
}

export enum ChaseScheduleTimeUnit {
  MINUTE = 1,
  HOUR = 2,
  DAY = 3,
}

export enum ChaseScheduleTimeLimitType {
  ALL = 1,
  LIMIT = 2,
}

export enum ChaseScheduleFilterJoin {
  AND = 1,
  OR = 2,
}

export enum ChaseScheduleFilterType {
  TAG = 1,
  PHONE = 3,
  ORDER = 4,
}

export enum ChaseScheduleComparison {
  EQUAL = 1,
  NOT_EQUAL = 2,
  NONE = 7,
  ANY = 8,
}

export enum ChaseScheduleActionType {
  ADD_TAG = 1,
  REMOVE_TAG = 2,
}

export interface IChaseScheduleTimeLimit {
  type: ChaseScheduleTimeLimitType;
  limit: {
    from: {h: string; m: string};
    to: {h: string; m: string};
  };
}

export interface IChaseScheduleConfig {
  timeLimit: IChaseScheduleTimeLimit;
  after: {
    number: number;
    unit: ChaseScheduleTimeUnit;
  };
  sendLimit?: number;
  isImportExcel?: number;
  fileName?: string;
}

export interface IChaseScheduleCondition {
  type: number;
  eq: number;
  content: {
    tagIds: string[];
    tagNames: string[];
    phones: string[];
  };
}

export interface IChaseScheduleConditions {
  filterType: ChaseScheduleFilterJoin;
  filters: IChaseScheduleCondition[];
}

export interface IChaseScheduleAction {
  type: ChaseScheduleActionType;
  content: {
    tagIds: string[];
    tagNames: string[];
  };
}

export interface IChaseScheduleApiItem {
  id: string | number;
  name?: string;
  channel?: number;
  businessId?: string | number;
  pageIds?: string[];
  scriptCampaignId?: string | number;
  status?: number;
  type?: number;
  schedule?: string | Partial<IChaseScheduleConfig>;
  actions?: string | IChaseScheduleAction[] | null;
  conditions?: string | Partial<IChaseScheduleConditions>;
  scriptMessageId?: string | number;
  triggerType?: number;
  manualSendAt?: number;
  runStatus?: number;
  sendStatus?: number;
  sendType?: number;
  createdById?: string | number | null;
  createdAt?: number;
}

export interface IChaseSchedule extends Omit<IChaseScheduleApiItem, 'schedule' | 'actions' | 'conditions'> {
  schedule: IChaseScheduleConfig;
  actions: IChaseScheduleAction[];
  conditions: IChaseScheduleConditions;
  totalSent?: number;
}

export interface IChaseScheduleListData {
  result: IChaseScheduleApiItem[];
  next: string | null;
}

export interface IGetChaseCampaignParams {
  businessId: string | number;
  campaignId: string | number;
  fields?: string[];
}

export interface IListChaseScheduleParams {
  businessId: string | number;
  scriptCampaignId?: string | number;
  type?: number;
  channels?: number[];
  pageIds?: string[];
  startCreated?: number;
  endCreated?: number;
  startSend?: number;
  endSend?: number;
  status?: number;
  next?: string;
}

export interface ICountSentChaseMessagesParams {
  businessId?: string | number;
  scriptCampaignId?: string | number;
}

export interface IChaseCountSentData {
  data: unknown;
}

export interface IAddChaseScheduleParams {
  businessId: string | number;
  schedule: string;
}

export interface IUpdateSingleChaseScheduleParams {
  businessId: string | number;
  scheduleId: string | number;
  schedule: string;
}

export interface IUpdateBulkChaseScheduleParams {
  businessId: string | number;
  scheduleIds: Array<string | number>;
  updateArray: Array<{key: 'status'; value: ChaseScheduleStatus}>;
}

export type IUpdateChaseScheduleParams = IUpdateSingleChaseScheduleParams | IUpdateBulkChaseScheduleParams;

export interface IRemoveChaseScheduleParams {
  businessId: string | number;
  scheduleId: string | number;
}

export interface IImportChaseLogsParams {
  businessId: string | number;
  scriptScheduleId: string | number;
  pageId: string | number;
  rows: string[];
}

export interface IImportChaseLogsData {
  created: number;
}

export interface IDeleteChaseImportLogsParams {
  businessId: string | number;
  scriptScheduleId: string | number;
}

export interface IDeleteChaseImportLogsData {
  msg: string;
  deleted: number;
}

export interface IViewChaseQueueParams {
  businessId: string | number;
  conversationId?: string;
  scriptScheduleId?: string | number;
  status?: number;
  sentStatus?: number;
  next?: string;
}

export interface IChaseQueueItem {
  id: string | number;
  conversationId?: string;
  scriptScheduleId?: string | number;
  status?: number;
  sentStatus?: number;
  scheduledAt?: number;
}

export interface IViewChaseQueueData {
  result: IChaseQueueItem[];
  next: string | null;
}

export interface IChaseCampaign {
  id: string | number;
  businessId?: string | number;
  name: string;
  channel: number;
  pageIds?: string[];
  startTimeType?: number;
  favorite?: number | null;
  totalMessages?: number;
  createdAt?: number;
  lastUpdatedAt?: number | null;
}

export interface IChaseCampaignListData {
  result: IChaseCampaign[];
  next: string | null;
}

export interface IAddChaseCampaignData {
  item: IChaseCampaign;
}

export interface IChaseCampaignMutationData {
  message: boolean;
}

export interface IListChaseCampaignParams {
  businessId: string | number;
  next?: string;
}

export interface IAddChaseCampaignParams {
  businessId: string | number;
  name: string;
  channel: number;
  pageIds?: string[];
}

export interface IUpdateChaseCampaignParams {
  businessId: string | number;
  campaignId: string | number;
  name?: string;
  favorite?: number;
  startTimeType?: number;
  pageIds?: string[];
}

export interface IRemoveChaseCampaignParams {
  businessId: string | number;
  campaignId: string | number;
}

export enum ChaseMessageWindowType {
  IN_24_HOURS = 1,
  OUT_24_HOURS = 2,
}

export interface IChaseMessageContent {
  type: ChaseMessageWindowType;
  messagingTag: string;
  messages: any[];
  keysValue: boolean;
  utilityTemplateIds: string[];
  utilityMessage: boolean;
}

export interface IChaseMessage {
  id?: string;
  name: string;
  content: IChaseMessageContent;
  businessId?: string | number;
  createdById?: string | number | null;
  createdAt?: number;
}

export enum ChaseQueueStatus {
  DONE = 1,
  FAIL = 2,
  PENDING = 3,
  WAIT_RETRY = 4,
  RUNNING = 5,
  IGNORE = 6,
}

export interface IChaseReportParams {
  businessId: string | number;
  scriptScheduleId: string | number;
  fromTime: number;
  toTime: number;
  act?: 'getreason';
  conversationId?: string;
  next?: string;
}

export interface IChaseReportSummaryItem {
  status: ChaseQueueStatus;
  count: number;
}

export interface IChaseReportMetric {
  uniqueUsers: number;
  totalSent: number;
}

export interface IChaseReportSeenMetric {
  uniqueUsers: number;
  totalSeen: number;
}

export interface IChaseReportSummary {
  sentSuccess: IChaseReportMetric;
  sentFailed: IChaseReportMetric;
  seenUsers: IChaseReportSeenMetric;
}

export interface IChaseReportLogItem {
  id?: string | number;
  conversationId?: string;
  scheduledAt?: number;
  reScheduleAt?: number;
  logResponse?: string | Record<string, unknown> | null;
  errorCode?: string;
  message?: string;
}

export interface IChaseReportData {
  data?: IChaseReportSummary | Record<string, unknown>;
  result?: IChaseReportLogItem[];
  next?: string | null;
}
